import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import AutoSizeTextarea from '../components/AutoSizeTextarea';
import BackIconButton from '../components/BackIconButton';
import { computeDocumentState, formatDateBritish, DOCUMENT_STATES } from '../lib/compliance';
import { pickLiveVersion } from '../lib/documentDrafts';
import { loadDocumentStream } from '../lib/complianceEvents';
import { nudgeEligibility, nudgeBlockedText, nudgeInputsFromStream } from '../lib/complianceNudge';
import { categoryLabel } from '../lib/supplierDisplay';
// Decisions live in one place so this page and the document detail page can't
// drift apart (detail-page §6).
import {
  approveDocument, requestInfoOnDocument, rejectDocument, nudgeSupplier,
  REQUEST_INFO_REASONS, REJECT_REASONS, MIN_NOTES_LENGTH, reasonLabel,
} from '../lib/complianceDecisions';
import { recomputeEcoBadge } from '../lib/ecoAward';
import { OttoAward, OttoLoading } from '../components/otto';

// Mirror of the supplier-portal viewer's MIME logic — images render
// inline via <img>, everything else via <iframe>. Excludes HEIC.
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg']);
const isImageFile = (name) => {
  if (!name) return false;
  const dot = name.lastIndexOf('.');
  if (dot < 0) return false;
  return IMAGE_EXTS.has(name.slice(dot + 1).toLowerCase());
};

// Signed hosted-form documents are stored as .html. Supabase serves them with
// an unreliable content-type, so <iframe src=storageUrl> renders the raw source
// as text instead of the document. Fetch the content and render via srcDoc
// (same fix as AttachmentViewerModal).
const isHtmlDoc = (name, url) => /\.html?(\?|$)/i.test(name || '') || /\.html?(\?|$)/i.test(url || '');

function HtmlDocFrame({ url, title }) {
  const [html, setHtml] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setHtml(null); setFailed(false);
    fetch(url)
      .then(r => { if (!r.ok) throw new Error('fetch'); return r.text(); })
      .then(t => { if (alive) setHtml(t); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [url]);
  const frameStyle = { width: '100%', height: 'calc(100vh - 140px)', border: 0, background: '#fff' };
  if (failed) {
    return (
      <div className="font-['Metropolis'] text-[13px] text-fusion-mid-grey text-center p-6">
        Couldn’t load the document here. <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#2E8FA1] underline">Open it in a new tab</a>.
      </div>
    );
  }
  if (html == null) return <div className="font-['Metropolis'] text-[13px] text-fusion-mid-grey p-6">Loading document…</div>;
  return <iframe title={title} srcDoc={html} sandbox="" style={frameStyle} />;
}

const RECENT_DAYS = 30;
const POST_ACTION_DELAY_MS = 1000;

const daysAgo = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t) / (24 * 60 * 60 * 1000));
};

export default function AdminComplianceReview() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();

  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [reviewerName, setReviewerName] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]); // reject/clarify conversation
  // The document's event stream, used ONLY to work out whether a nudge is due.
  // It carries the last nudge marker, without which the 7-day cooldown can't
  // hold and this page could nudge again minutes after the detail page did.
  const [stream, setStream] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Action panel state machine. 'idle' shows the three action
  // buttons. 'request_info' / 'reject' expand into their form.
  // 'submitting' / 'success' / 'error' are transient.
  const [actionState, setActionState] = useState('idle');
  const [submittingAction, setSubmittingAction] = useState(null);
  // Captain-Otto celebration when an approval tips a supplier to Oak Status.
  const [oakCelebrate, setOakCelebrate] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  // Optional comment to the supplier, sent with an approval (into the thread).
  const [approveComment, setApproveComment] = useState('');
  // Reviewer-confirmed expiry (yyyy-mm-dd) captured in the confirm_expiry step
  // before an approval on an expiry-bearing document (§3).
  const [confirmExpiry, setConfirmExpiry] = useState('');
  const [confirmText, setConfirmText] = useState('');
  // Admin/super-user-only document archive (two-step inline confirm).
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  // Reject/clarify conversation for this document, oldest first.
  const loadThread = useCallback(async (supplierId, documentKey) => {
    if (!supplierId || !documentKey) { setThreadMessages([]); return; }
    const { data } = await supabase
      .from('compliance_clarification_messages')
      .select('id, author, body, attachment_url, attachment_name, created_at')
      .eq('supplier_id', supplierId)
      .eq('document_key', documentKey)
      .order('created_at', { ascending: true });
    setThreadMessages(data || []);
    // Same source the detail page uses, so both agree on when we last asked and
    // last nudged.
    setStream(await loadDocumentStream(supplierId, documentKey));
  }, []);

  // The document's human name — needed by the decision calls above the render,
  // so it's derived here rather than alongside the other display values.
  const docLabel = meta?.document_name || doc?.document_key || 'compliance document';

  const loadDoc = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: docErr } = await supabase
      .from('supplier_onboarding_documents')
      .select('*, suppliers(id, supplier_name, category, status, risk_flag)')
      .eq('id', docId)
      .single();
    if (docErr || !data) {
      setError(docErr?.message || 'Document not found.');
      setLoading(false);
      return;
    }
    setDoc(data);
    setSupplier(data.suppliers || null);
    loadThread(data.supplier_id, data.document_key);
    // Several rows share a document key once it has versions (live, superseded,
    // and possibly an unpublished draft) — take the live one. .maybeSingle()
    // would error the moment a second version exists.
    const { data: fd } = await supabase
      .from('fusion_documents')
      .select('compliance_doc_key, document_name, description, supplier_provides_expiry, status, version_value')
      .eq('compliance_doc_key', data.document_key);
    setMeta(pickLiveVersion(fd));
    if (data.reviewed_by) {
      const { data: rev } = await supabase
        .from('internal_users')
        .select('full_name')
        .eq('id', data.reviewed_by)
        .maybeSingle();
      setReviewerName(rev?.full_name || null);
    } else {
      setReviewerName(null);
    }
    setLoading(false);
  }, [docId]);

  useEffect(() => { loadDoc(); }, [loadDoc]);

  // Reviewer reason snippets are admin-editable via Admin → List Values
  // ('compliance_reject_reason' / 'compliance_request_info_reason'). Load the
  // active set; fall back to the built-in labels if the lists are empty (fresh
  // install / migration not yet run). Stored as label strings going forward —
  // reasonLabel still decodes any legacy key-based rows. §3.
  const [reasonChoices, setReasonChoices] = useState({
    reject: REJECT_REASONS.map(r => r.label),
    request_info: REQUEST_INFO_REASONS.map(r => r.label),
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('list_values')
        .select('list_name, value, display_order')
        .in('list_name', ['compliance_reject_reason', 'compliance_request_info_reason'])
        .order('display_order');
      if (cancelled || !data) return;
      const reject = data.filter(r => r.list_name === 'compliance_reject_reason').map(r => r.value);
      const requestInfo = data.filter(r => r.list_name === 'compliance_request_info_reason').map(r => r.value);
      setReasonChoices({
        reject: reject.length ? reject : REJECT_REASONS.map(r => r.label),
        request_info: requestInfo.length ? requestInfo : REQUEST_INFO_REASONS.map(r => r.label),
      });
    })();
    return () => { cancelled = true; };
  }, []);

  // Reset the action form whenever we switch documents (auto-navigate
  // path uses replace+navigate which fires a fresh load).
  //
  // EVERYTHING the reviewer typed has to go, not just some of it: this page
  // stays mounted as it advances through the queue, so anything left behind is
  // aimed at the next supplier's document. approveComment and the archive
  // confirm were missing, which is how "Nice signature" followed a reviewer
  // from one document to the next.
  useEffect(() => {
    setActionState('idle');
    setReason('');
    setNotes('');
    setApproveComment('');
    setConfirmExpiry('');
    setConfirmText(null);
    setConfirmingArchive(false);
  }, [docId]);

  const state = doc ? computeDocumentState(doc) : null;
  // Does approving this doc need an expiry confirmation step? True when the
  // supplier entered an expiry, or the doc type is configured to carry one
  // (so the reviewer sets it even if the supplier left it blank). §3.
  const needsExpiryConfirm = !!(doc?.expiry_date || meta?.supplier_provides_expiry);
  const isAwaiting = state === DOCUMENT_STATES.AWAITING_REVIEW;
  const isApproved = state === DOCUMENT_STATES.APPROVED || state === DOCUMENT_STATES.EXPIRING_SOON;
  const isClarification = state === DOCUMENT_STATES.NEEDS_CLARIFICATION;
  const isRejected = doc?.review_decision === 'rejected';
  const isExpired = state === DOCUMENT_STATES.EXPIRED;

  // Nudge eligibility, from the same derivation the detail page uses so the
  // two screens can't disagree about whether one is due (nudgeInputsFromStream).
  const nudgeInputs = nudgeInputsFromStream(stream, {
    withSupplier: isClarification || isRejected,
    // The row's own record of when we asked — see nudgeInputsFromStream.
    fallbackAskedAt: doc?.reviewed_at || null,
  });
  const nudge = nudgeEligibility(nudgeInputs);
  // nudgeBlockedText, not nudgeExplanation: this page shows the button disabled
  // rather than hiding it, so it must always be able to say why.
  const nudgeNote = nudge.canNudge ? null : nudgeBlockedText(nudge);

  const runNudge = async () => {
    if (!nudge.canNudge || submittingAction) return;
    setSubmittingAction('nudge');
    const res = await nudgeSupplier({
      doc,
      supplier,
      documentLabel: docLabel,
      question: nudgeInputs.lastAsk?.body || doc?.review_notes || null,
      askedAt: nudgeInputs.lastAsk?.at || null,
      actorId: currentInternalUser?.id || null,
    });
    setSubmittingAction(null);
    if (!res?.emailed) {
      setError(res?.noAddress
        ? 'This supplier has no active portal user, so there was nobody to email.'
        : 'The nudge could not be sent.');
      return;
    }
    // Reload so the nudge marker lands in the stream and the cooldown starts.
    loadThread(doc.supplier_id, doc.document_key);
  };

  // "Next document" — fetches the oldest other Awaiting Review row
  // and navigates to it. Returns null if none left.
  const navigateToNext = useCallback(async () => {
    const { data } = await supabase
      .from('supplier_onboarding_documents')
      .select('id, supplier_uploaded_at, expiry_approved_at, review_decision')
      .not('supplier_uploaded_at', 'is', null)
      .is('expiry_approved_at', null)
      .is('review_decision', null)
      .neq('id', docId)
      .order('supplier_uploaded_at', { ascending: true })
      .limit(1);
    const next = (data || [])[0];
    if (next) {
      navigate(`/compliance/${next.id}/review`, { replace: true });
    } else {
      navigate('/compliance');
    }
  }, [docId, navigate]);

  const fireConfirmation = useCallback((text, andThen) => {
    setConfirmText(text);
    setTimeout(() => {
      setConfirmText(null);
      andThen?.();
    }, POST_ACTION_DELAY_MS);
  }, []);

  // `confirmedExpiry` (yyyy-mm-dd | null) is the reviewer-confirmed expiry from
  // the confirm_expiry step. When it differs from the supplier-entered date the
  // corrected value is written to expiry_date and an `override` audit row records
  // the change; when the doc carries no expiry it's null and untouched. §3.
  const handleApprove = useCallback(async (confirmedExpiry) => {
    if (!doc) return;
    setSubmittingAction('approve');
    // The write, the thread comment, the event, the audit rows and the email
    // all live in complianceDecisions so the detail page does exactly the same
    // thing. This page keeps what's ITS job: the queue advance, the eco badge
    // and the celebration.
    const emailRes = await approveDocument({
      doc,
      supplier,
      documentLabel: docLabel,
      confirmedExpiry,
      comment: approveComment,
      actorId: currentInternalUser?.id || null,
      source: 'review_page',
    });
    setSubmittingAction(null);
    if (emailRes?.error) {
      setError(`Could not approve: ${emailRes.error}`);
      setTimeout(() => setError(null), 6000);
      return;
    }
    // The approval stands, but the comment never reached the supplier. Shown
    // in the same place as an error because "approved, and they can't see what
    // you wrote" needs reading before the queue advances past it.
    if (emailRes?.warning) {
      setError(emailRes.warning);
      setTimeout(() => setError(null), 8000);
    }
    setApproveComment('');
    setConfirmExpiry('');
    // Keep the Eco oak in sync (best-effort; no-op for non-eco docs or
    // suppliers without eco requirements). If THIS approval is the one
    // that tips the supplier to Oak Status, fire the Captain-Otto
    // celebration and hold on the doc until it finishes before advancing.
    const eco = await recomputeEcoBadge(supabase, doc.supplier_id).catch(() => null);
    // §5 — if the supplier has no active portal user, no email went out. Warn
    // the reviewer and stay on the doc rather than silently advancing.
    if (emailRes?.noAddress) {
      setError('Approved — but this supplier has no active portal user, so no email was sent. Add a portal user so they’re notified.');
      setTimeout(() => setError(null), 9000);
      fireConfirmation('Approved ✓', () => {});
    } else if (eco?.newlyOak) {
      setOakCelebrate(true);
      fireConfirmation('Approved ✓', () => {});
    } else {
      fireConfirmation('Approved ✓', navigateToNext);
    }
  }, [doc, supplier, docLabel, currentInternalUser?.id, approveComment, fireConfirmation, navigateToNext]);

  // Archive the uploaded compliance document (admin / super_user only). RESETS
  // the slot back to "outstanding" — the upload / review / approval fields are
  // cleared but, unlike the old hard-delete, the stored FILE is kept and a full
  // snapshot of the slot (file url + name + expiry) is written to the audit
  // 'archived' event, so it's recoverable rather than destroyed. The row (and
  // any not_required exemption) is kept, so the requirement still tracks. Badges
  // already awarded are NOT revoked here; use the supplier-page override. §3.
  const handleArchive = useCallback(async () => {
    if (!doc) return;
    setSubmittingAction('archive');
    const { error: archiveErr } = await supabase
      .from('supplier_onboarding_documents')
      .update({
        supplier_uploaded_at: null,
        supplier_uploaded_file_url: null,
        supplier_uploaded_file_name: null,
        expiry_date: null,
        expiry_approved_at: null,
        expiry_approved_by: null,
        review_decision: null,
        review_reason: null,
        review_notes: null,
        reviewed_at: null,
        reviewed_by: null,
        supplier_reply: null,
        supplier_reply_at: null,
        expiry_email_sent_at: null,
        expiry_warning_email_sent_at: null,
        expiry_warning_acknowledged_at: null,
        expiry_warning_acknowledged_by: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', doc.id);
    if (archiveErr) {
      setSubmittingAction(null);
      setError(`Could not archive: ${archiveErr.message}`);
      setTimeout(() => setError(null), 6000);
      return;
    }
    // Snapshot everything needed to restore the upload into the audit event —
    // the stored file itself is deliberately left in place. Best-effort; never
    // blocks the archive.
    supabase.from('compliance_audit_log').insert([{
      actor_id: currentInternalUser?.id || null,
      action: 'archived',
      supplier_id: doc.supplier_id,
      document_key: doc.document_key,
      meta: {
        fileName: doc.supplier_uploaded_file_name || null,
        fileUrl: doc.supplier_uploaded_file_url || null,
        expiryDate: doc.expiry_date || null,
        source: 'review_page',
      },
    }]).then(() => {});
    // Archiving an eco doc removes an approval — re-sync the oak (it may
    // step back down). Best-effort.
    recomputeEcoBadge(supabase, doc.supplier_id);
    setSubmittingAction(null);
    setConfirmingArchive(false);
    fireConfirmation('Document archived ✓', () => navigate('/compliance'));
  }, [doc, currentInternalUser?.id, fireConfirmation, navigate]);

  const handleRequestInfo = useCallback(async () => {
    if (!doc || !reason || notes.trim().length < MIN_NOTES_LENGTH) return;
    setSubmittingAction('request_info');
    const emailRes = await requestInfoOnDocument({
      doc,
      supplier,
      documentLabel: docLabel,
      reason,
      reasonLabel: reasonLabel(reason, REQUEST_INFO_REASONS),
      notes,
      actorId: currentInternalUser?.id || null,
      source: 'review_page',
    });
    setSubmittingAction(null);
    if (emailRes?.error) {
      setError(`Could not save: ${emailRes.error}`);
      setTimeout(() => setError(null), 6000);
      return;
    }
    if (emailRes?.noAddress) {
      setError('Saved — but this supplier has no active portal user, so no email was sent. Add a portal user so they’re notified.');
      setTimeout(() => setError(null), 9000);
      fireConfirmation('Request sent ✓', () => {});
    } else {
      fireConfirmation('Request sent ✓', navigateToNext);
    }
  }, [doc, supplier, docLabel, reason, notes, currentInternalUser?.id, fireConfirmation, navigateToNext]);

  const handleReject = useCallback(async () => {
    if (!doc || !reason || notes.trim().length < MIN_NOTES_LENGTH) return;
    setSubmittingAction('reject');
    const emailRes = await rejectDocument({
      doc,
      supplier,
      documentLabel: docLabel,
      reason,
      reasonLabel: reasonLabel(reason, REJECT_REASONS),
      notes,
      actorId: currentInternalUser?.id || null,
      source: 'review_page',
    });
    setSubmittingAction(null);
    if (emailRes?.error) {
      setError(`Could not save: ${emailRes.error}`);
      setTimeout(() => setError(null), 6000);
      return;
    }
    if (emailRes?.noAddress) {
      setError('Rejected — but this supplier has no active portal user, so no email was sent. Add a portal user so they’re notified.');
      setTimeout(() => setError(null), 9000);
      fireConfirmation('Rejected ✓', () => {});
    } else {
      fireConfirmation('Rejected ✓', navigateToNext);
    }
  }, [doc, supplier, docLabel, reason, notes, currentInternalUser?.id, fireConfirmation, navigateToNext]);

  const handleRevokeApproval = useCallback(async () => {
    if (!doc) return;
    const now = new Date().toISOString();
    const { error: updateErr } = await supabase
      .from('supplier_onboarding_documents')
      .update({
        expiry_approved_at: null,
        expiry_approved_by: null,
        reviewed_at: now,
        reviewed_by: currentInternalUser?.id || null,
        updated_at: now,
      })
      .eq('id', doc.id);
    if (updateErr) {
      setError(`Could not revoke: ${updateErr.message}`);
      setTimeout(() => setError(null), 6000);
      return;
    }
    // No supplier email for revoke — brief flags it as admin
    // correction. Reload the doc to surface the new state.
    loadDoc();
  }, [doc, currentInternalUser?.id, loadDoc]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  const sidebar = (
    <Sidebar currentInternalUser={currentInternalUser} />
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex">
        {sidebar}
        <div className="flex-1 flex items-center justify-center">
          {/* OttoLoading, not the inline scene: this centres in the whole
              content area with nothing else in it, so Otto IS the scene and the
              flying hover reads standalone. The inline bar version is for a
              panel filling under chrome that stays. */}
          <OttoLoading size="large" message="Loading document" />
        </div>
      </div>
    );
  }

  // Admin / super-user gate — matches the list page at /compliance.
  // Non-admin users land on the restricted panel instead of being
  // able to approve / reject by direct URL.
  if (currentInternalUser && !isAdmin(currentInternalUser)) {
    return (
      <div className="min-h-screen bg-canvas flex">
        {sidebar}
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="font-['Metropolis'] text-[10px] font-bold tracking-[3px] uppercase text-[#7A4E10] mb-3">Restricted</p>
          <p className="font-['Metropolis'] text-lg text-ink mb-6 max-w-md">Compliance is restricted to Admin and Super User accounts.</p>
          <button onClick={() => navigate('/dashboard')} className="font-['Metropolis'] text-xs font-semibold tracking-[2px] uppercase text-fusion-forest border border-[#92D400] rounded-full px-5 py-2 hover:bg-active-green/10 transition-colors">
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen bg-canvas flex">
        {sidebar}
        <div className="flex-1 p-12">
          <div className="mb-4"><BackIconButton to="/compliance" label="Back to queue" /></div>
          <p className="font-['Metropolis'] text-sm text-[#E0290A]">{error || 'Not found.'}</p>
        </div>
      </div>
    );
  }

  const docDescription = meta?.description || null;
  const fileUrl = doc.supplier_uploaded_file_url || '';
  const fileName = doc.supplier_uploaded_file_name || 'Document';
  const showImage = isImageFile(fileName);
  const uploadAgo = daysAgo(doc.supplier_uploaded_at);
  const recentApprovalDays = daysAgo(doc.expiry_approved_at);

  return (
    <div className="min-h-screen bg-canvas flex">
      {sidebar}

      {/* Oak Status reached → Captain Otto celebration, then advance to
          the next doc once it finishes. No milestone label (Oak isn't a
          per-staff milestone). */}
      <OttoAward airshow show={oakCelebrate} onComplete={() => { setOakCelebrate(false); navigateToNext(); }} />

      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="px-8 pt-6 pb-4 border-b border-line flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <BackIconButton to="/compliance" label="Back to queue" />
            <div className="font-['Metropolis'] text-[11px] uppercase tracking-[0.18em] text-[#51534A] flex items-center gap-1.5 min-w-0">
              <button type="button" onClick={() => navigate('/compliance')} className="hover:text-fusion-mid-grey transition-colors">Compliance</button>
              <span>›</span>
              <span className="text-fusion-mid-grey truncate">{supplier?.supplier_name || '—'}</span>
              <span>·</span>
              <span className="text-fusion-mid-grey truncate">{docLabel}</span>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Full chronological history — every version's file + the whole
                decision/conversation stream (document-detail-page §1). */}
            <button
              type="button"
              onClick={() => navigate(`/compliance/${docId}/detail`)}
              className="font-['Metropolis'] text-xs font-bold uppercase tracking-[0.15em] text-[#2E8FA1] hover:text-active-green transition-colors">
              {/* "Document history" per versioning brief B §1: this is ONE
                  supplier's stream for this document. Version history (one
                  document, all suppliers) is a different page — keeping the
                  names apart keeps the two histories apart. */}
              Document history →
            </button>
            {isAwaiting && (
              <button
                type="button"
                onClick={navigateToNext}
                className="font-['Metropolis'] text-xs font-bold uppercase tracking-[0.15em] text-fusion-forest hover:opacity-85 transition-opacity">
                Next document →
              </button>
            )}
          </div>
        </div>

        {/* Mobile fallback note. The brief flags the review page as
            desktop-only because PDF preview is unreliable on small
            screens. Tailwind's `md:hidden` shows this only below 768px. */}
        <div className="md:hidden px-8 py-12 text-center">
          <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed">
            This page is best viewed on desktop — PDF preview doesn't
            reliably fit a phone screen. Open this URL on a laptop
            to review the document.
          </p>
        </div>

        {/* Body — two-column on desktop. */}
        <div className="hidden md:flex flex-1 min-h-0">
          {/* Left: viewer */}
          <div className="w-3/5 border-r border-line bg-white flex items-center justify-center p-5">
            {fileUrl ? (
              showImage ? (
                <img src={fileUrl} alt={fileName} style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 140px)', display: 'block' }} />
              ) : isHtmlDoc(fileName, fileUrl) ? (
                <HtmlDocFrame url={fileUrl} title={fileName} />
              ) : (
                <iframe src={fileUrl} title={fileName} style={{ width: '100%', height: 'calc(100vh - 140px)', border: 0, background: '#fafafa' }} />
              )
            ) : (
              <p className="font-['Metropolis'] text-sm text-[#51534A] italic">No file uploaded.</p>
            )}
          </div>

          {/* Right: metadata + actions */}
          <div className="w-2/5 flex flex-col">
            <div className="p-6 overflow-y-auto flex-1">
              <h1 className="font-['Metropolis'] text-2xl font-bold text-ink mb-2">{docLabel}</h1>
              {docDescription && <p className="font-['Metropolis'] text-sm text-[#51534A] leading-snug mb-6">{docDescription}</p>}

              <MetaBlock label="Supplier" rows={[
                { key: 'Business', val: (
                  <button type="button" onClick={() => navigate(`/suppliers/${supplier?.id}`)} className="text-fusion-forest hover:underline bg-transparent border-0 p-0 cursor-pointer font-['Metropolis']">
                    {supplier?.supplier_name || '—'}
                  </button>
                ) },
                // §5d: a bare dash read as a broken field. It's an absent
                // value, so say so (lib/supplierDisplay).
                { key: 'Category', val: categoryLabel(supplier) },
                { key: 'Risk tier', val: supplier?.risk_flag ? supplier.risk_flag.charAt(0).toUpperCase() + supplier.risk_flag.slice(1) : '—' },
              ]} />

              <MetaBlock label="Document" rows={[
                { key: 'Expiry entered', val: doc.expiry_date ? formatDateBritish(doc.expiry_date) : '—' },
                { key: 'Uploaded', val: doc.supplier_uploaded_at ? `${formatDateBritish(doc.supplier_uploaded_at.split('T')[0])}${uploadAgo != null ? ` · ${uploadAgo} days ago` : ''}` : '—' },
                { key: 'File', val: fileName },
              ]} />

              {/* Supplier's note attached to this upload (optional). Neutral —
                  it's context, not an alert. */}
              {doc.supplier_upload_note && doc.supplier_upload_note.trim() && (
                <div className="rounded-md px-3 py-3 mb-5 font-['Metropolis'] text-[12px]" style={{ backgroundColor: 'rgba(46,143,161,0.06)', border: '1px solid rgba(46,143,161,0.28)' }}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2E8FA1] mb-1">Note from the supplier</div>
                  <div className="text-ink whitespace-pre-wrap">{doc.supplier_upload_note}</div>
                </div>
              )}

              {/* Audit context for non-awaiting states */}
              {isApproved && (
                <AuditNote tone="green">
                  Approved by {reviewerName || 'Fusion'} on {doc.expiry_approved_at ? formatDateBritish(doc.expiry_approved_at.split('T')[0]) : '—'}.
                </AuditNote>
              )}
              {isExpired && (
                <AuditNote tone="expired">
                  Expired on {doc.expiry_date ? formatDateBritish(doc.expiry_date) : '—'} (approved by {reviewerName || 'Fusion'}).
                </AuditNote>
              )}
              {isRejected && (
                <AuditNote tone="red">
                  Rejected by {reviewerName || 'Fusion'} on {doc.reviewed_at ? formatDateBritish(doc.reviewed_at.split('T')[0]) : '—'} — {reasonLabel(doc.review_reason, REJECT_REASONS)}.
                  {threadMessages.length === 0 && doc.review_notes && <div className="mt-2 text-fusion-mid-grey whitespace-pre-wrap">{doc.review_notes}</div>}
                </AuditNote>
              )}
              {isClarification && (
                <AuditNote tone="amber">
                  Awaiting clarification from supplier since {doc.reviewed_at ? formatDateBritish(doc.reviewed_at.split('T')[0]) : '—'} — {reasonLabel(doc.review_reason, REQUEST_INFO_REASONS)}.
                  {threadMessages.length === 0 && doc.review_notes && <div className="mt-2 text-fusion-mid-grey whitespace-pre-wrap">{doc.review_notes}</div>}
                  {/* Nudge, not chase — they DID send something, we asked a
                      question, and they've gone quiet. Guardrails live in
                      lib/complianceNudge: not before 3 working days of silence,
                      once per 7 days, and it disappears the moment they reply.
                      When it can't be sent, the reason is stated rather than
                      leaving a dead button. */}
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={runNudge}
                      disabled={!nudge.canNudge || submittingAction === 'nudge'}
                      className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-4 py-2 border border-[#7A4E10]/40 text-[#7A4E10] bg-white hover:bg-[#E89A2E]/10 disabled:opacity-45 disabled:cursor-not-allowed transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7A4E10]">
                      {submittingAction === 'nudge' ? 'Sending…' : 'Send nudge'}
                    </button>
                    {nudgeNote && (
                      <span className="font-['Metropolis'] text-[11px] text-fusion-mid-grey">{nudgeNote}</span>
                    )}
                  </div>
                </AuditNote>
              )}
              {/* Replying to a clarification clears review_decision (the doc
                  returns to Awaiting review), so surface the exchange here too
                  — otherwise the admin re-reviews with no record of what they
                  asked or what the supplier answered. */}
              {isAwaiting && (threadMessages.length > 0 || doc.supplier_reply) && (
                <AuditNote tone="amber">
                  Back with you for review{doc.review_reason ? ` — you asked about ${reasonLabel(doc.review_reason, REQUEST_INFO_REASONS)}` : ''}.
                  {threadMessages.length === 0 && doc.supplier_reply && (
                    <div className="mt-3 pt-3 border-t border-line">
                      <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-fusion-mid-grey mb-1">
                        Supplier's reply{doc.supplier_reply_at ? ` · ${formatDateBritish(doc.supplier_reply_at.split('T')[0])}` : ''}
                      </div>
                      <div className="text-ink whitespace-pre-wrap">{doc.supplier_reply}</div>
                    </div>
                  )}
                </AuditNote>
              )}
              {/* Full reject/clarify conversation — every round, both sides. */}
              {(isRejected || isClarification || isAwaiting) && threadMessages.length > 0 && (
                <AdminClarificationThread messages={threadMessages} reviewerName={reviewerName} />
              )}
            </div>

            {/* Action area */}
            <div className="border-t border-line p-6 bg-white/60">
              {confirmText ? (
                <p className="text-center font-['Metropolis'] text-base font-bold text-fusion-forest">{confirmText}</p>
              ) : isAwaiting ? (
                actionState === 'idle' ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <ActionButton
                      variant="solid" accent="#92D400" textOnAccent="#131313"
                      onClick={() => {
                        // Expiry-bearing docs route through a confirm step so the
                        // reviewer signs off (or corrects) the date before it's
                        // trusted; others approve straight away. §3.
                        if (needsExpiryConfirm) {
                          setConfirmExpiry(doc.expiry_date || '');
                          setActionState('confirm_expiry');
                        } else {
                          handleApprove();
                        }
                      }}
                      disabled={submittingAction !== null}>
                      {submittingAction === 'approve' ? 'Approving…' : 'Approve'}
                    </ActionButton>
                    <ActionButton variant="outline" accent="#E89A2E" onClick={() => { setReason(''); setNotes(''); setActionState('request_info'); }}>
                      Request info
                    </ActionButton>
                    <ActionButton variant="outline" accent="#E0290A" onClick={() => { setReason(''); setNotes(''); setActionState('reject'); }}>
                      Reject
                    </ActionButton>
                  </div>
                ) : actionState === 'confirm_expiry' ? (
                  <ExpiryConfirmForm
                    enteredExpiry={doc.expiry_date || null}
                    value={confirmExpiry}
                    setValue={setConfirmExpiry}
                    comment={approveComment}
                    setComment={setApproveComment}
                    submitting={submittingAction === 'approve'}
                    onCancel={() => { setActionState('idle'); setConfirmExpiry(''); setApproveComment(''); }}
                    onConfirm={() => handleApprove(confirmExpiry)}
                  />
                ) : (
                  <ActionForm
                    mode={actionState}
                    reason={reason}
                    notes={notes}
                    reasonChoices={actionState === 'request_info' ? reasonChoices.request_info : reasonChoices.reject}
                    setReason={setReason}
                    setNotes={setNotes}
                    onCancel={() => { setActionState('idle'); setReason(''); setNotes(''); }}
                    onConfirm={actionState === 'request_info' ? handleRequestInfo : handleReject}
                    submittingAction={submittingAction}
                  />
                )
              ) : isApproved && recentApprovalDays != null && recentApprovalDays <= RECENT_DAYS ? (
                <button
                  type="button"
                  onClick={handleRevokeApproval}
                  className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-[#E0290A] bg-transparent border border-line hover:border-[#E0290A]/60 rounded-full px-4 py-2 transition-colors">
                  Revoke approval
                </button>
              ) : (
                <p className="font-['Metropolis'] text-[12px] text-[#51534A] italic">
                  This document isn't currently awaiting review.
                </p>
              )}

              {/* Archive document — admin / super_user only, shown for any
                  uploaded doc regardless of review state. Two-step inline
                  confirm; resets the slot to outstanding but keeps the stored
                  file + a restorable snapshot in the audit trail. §3. */}
              {!confirmText && isAdmin(currentInternalUser) && doc?.supplier_uploaded_at && (
                <div className="mt-5 pt-4 border-t border-line/60">
                  {confirmingArchive ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-['Metropolis'] text-[12px] text-fusion-mid-grey flex-1 min-w-[200px]">
                        Archive this uploaded document? The slot returns to outstanding, but the file is kept and the upload is recorded in the activity log so it can be recovered.
                      </span>
                      <button
                        type="button"
                        onClick={handleArchive}
                        disabled={submittingAction !== null}
                        className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-ink bg-[#E89A2E] hover:bg-[#cf8420] rounded-full px-4 py-2 transition-colors disabled:opacity-50">
                        {submittingAction === 'archive' ? 'Archiving…' : 'Archive document'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingArchive(false)}
                        disabled={submittingAction !== null}
                        className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border border-line rounded-full px-4 py-2 transition-colors">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmingArchive(true)}
                      className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-[#E89A2E] bg-transparent border border-line hover:border-[#E89A2E]/60 rounded-full px-4 py-2 transition-colors inline-flex items-center gap-1.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="21 8 21 21 3 21 3 8" />
                        <rect x="1" y="3" width="22" height="5" />
                        <line x1="10" y1="12" x2="14" y2="12" />
                      </svg>
                      Archive document
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------

// Confirm-expiry step shown before approving an expiry-bearing document (§3).
// Pre-filled with the supplier-entered date; the reviewer confirms or corrects
// it. Approval is blocked until a date is set, so an expiry doc can't be
// approved with a missing expiry.
function ExpiryConfirmForm({ enteredExpiry, value, setValue, comment, setComment, submitting, onCancel, onConfirm }) {
  const changed = (value || null) !== (enteredExpiry || null);
  return (
    <div className="space-y-3">
      <div>
        <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-fusion-forest mb-1">Confirm expiry date</div>
        <p className="font-['Metropolis'] text-[12px] text-fusion-mid-grey leading-snug">
          {enteredExpiry
            ? <>The supplier entered <span className="font-semibold text-ink">{formatDateBritish(enteredExpiry)}</span>. Confirm it matches the document, or correct it before approving.</>
            : <>This document type carries an expiry, but the supplier didn't provide one. Enter the expiry shown on the document before approving.</>}
        </p>
      </div>
      <input
        type="date"
        value={value || ''}
        onChange={(e) => setValue(e.target.value)}
        className="font-['Metropolis'] text-sm text-ink bg-white border border-line rounded-md px-3 py-2 focus:outline-none focus:border-fusion-forest" />
      {changed && value && (
        <p className="font-['Metropolis'] text-[11px] text-[#E89A2E]">Approving will update the expiry to {formatDateBritish(value)}.</p>
      )}
      {setComment && (
        <div>
          <label htmlFor="approve-comment" className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#51534A] mb-1.5">
            Comment to the supplier <span className="text-[#9BA08C] normal-case tracking-normal font-semibold">· optional</span>
          </label>
          <textarea
            id="approve-comment"
            value={comment || ''}
            onChange={(e) => setComment(e.target.value)}
            disabled={submitting}
            rows={2}
            placeholder="e.g. Thanks — all good, approved."
            className="w-full font-['Metropolis'] text-[13px] text-ink bg-white border border-line rounded-md px-3 py-2 focus:outline-none focus:border-fusion-forest disabled:opacity-50 resize-y" />
          <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1">Added to the conversation on this document, visible to the supplier.</p>
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap pt-1">
        <ActionButton variant="solid" accent="#92D400" textOnAccent="#131313" onClick={onConfirm} disabled={submitting || !value}>
          {submitting ? 'Approving…' : 'Confirm & approve'}
        </ActionButton>
        <button
          type="button" onClick={onCancel} disabled={submitting}
          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border border-line rounded-full px-4 py-2 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

function MetaBlock({ label, rows }) {
  return (
    <div className="mb-5">
      <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-fusion-forest mb-2">{label}</div>
      <div className="bg-white border border-line rounded-md px-3 py-2">
        {rows.map(row => (
          <div key={row.key} className="flex items-baseline justify-between py-1.5 border-b border-line last:border-b-0 gap-3">
            <span className="font-['Metropolis'] text-[11px] text-[#51534A] uppercase tracking-[0.15em]">{row.key}</span>
            <span className="font-['Metropolis'] text-[12px] text-ink font-semibold text-right">{row.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuditNote({ tone, children }) {
  // 'expired' reads near-black (lapsed/dead), distinct from red Outstanding,
  // matching the five-state document legend (build #13).
  const colour = tone === 'green' ? '#92D400' : tone === 'red' ? '#E0290A' : tone === 'expired' ? '#8E1B06' : '#E89A2E';
  const bg = tone === 'green' ? 'rgba(146,212,0,0.06)' : tone === 'red' ? 'rgba(224,41,10,0.06)' : tone === 'expired' ? 'rgba(58,59,51,0.06)' : 'rgba(232,154,46,0.06)';
  const border = tone === 'green' ? 'rgba(146,212,0,0.25)' : tone === 'red' ? 'rgba(224,41,10,0.25)' : tone === 'expired' ? 'rgba(58,59,51,0.25)' : 'rgba(232,154,46,0.25)';
  return (
    <div className="rounded-md px-3 py-3 font-['Metropolis'] text-[12px] mb-5" style={{ backgroundColor: bg, border: `1px solid ${border}`, color: colour }}>
      {children}
    </div>
  );
}

// The full reject/clarify conversation, every round in order. Admin messages
// align left ("Fusion 2K"), supplier replies right ("Supplier"), each with a
// date and any attached replacement file.
function AdminClarificationThread({ messages, reviewerName }) {
  if (!messages || messages.length === 0) return null;
  return (
    <div className="rounded-md px-4 py-3.5 mb-5 flex flex-col gap-3" style={{ backgroundColor: 'rgba(146,150,132,0.06)', border: '1px solid rgba(155,160,140,0.3)' }}>
      <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-fusion-mid-grey">Conversation</div>
      {messages.map((m) => {
        const isAdmin = m.author === 'admin';
        const when = m.created_at ? formatDateBritish(m.created_at.split('T')[0]) : null;
        return (
          <div key={m.id} className={`flex flex-col ${isAdmin ? 'items-start' : 'items-end'}`}>
            <div className="font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.18em] mb-0.5" style={{ color: isAdmin ? '#7A4E10' : '#2E8FA1' }}>
              {isAdmin ? (reviewerName || 'Fusion 2K') : 'Supplier'}{when ? ` · ${when}` : ''}
            </div>
            {m.body && (
              <div className="font-['Metropolis'] text-[12px] text-ink leading-snug whitespace-pre-wrap max-w-[80%] rounded-md px-3 py-2" style={{ backgroundColor: isAdmin ? 'rgba(232,154,46,0.08)' : 'rgba(46,143,161,0.08)' }}>
                {m.body}
              </div>
            )}
            {m.attachment_url && (
              <a href={m.attachment_url} target="_blank" rel="noopener noreferrer" className="font-['Metropolis'] text-[11px] font-semibold underline underline-offset-2 mt-1" style={{ color: '#2E8FA1' }}>
                {m.attachment_name || 'View attachment'}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ActionButton({ variant = 'neutral', accent, textOnAccent, onClick, disabled, children }) {
  const base = "font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] px-4 py-2 rounded-full transition-[filter,opacity] duration-150 disabled:opacity-50 disabled:cursor-not-allowed";
  if (variant === 'solid') {
    return (
      <button type="button" onClick={onClick} disabled={disabled} className={`${base} hover:brightness-110`}
        style={{ backgroundColor: accent, color: textOnAccent || '#F9FCF1', border: `1px solid ${accent}` }}>
        {children}
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${base} bg-transparent`}
      style={{ color: accent, border: `1px solid ${accent}` }}>
      {children}
    </button>
  );
}

function ActionForm({ mode, reason, notes, reasonChoices, setReason, setNotes, onCancel, onConfirm, submittingAction }) {
  const isClarify = mode === 'request_info';
  // Admin-editable label strings (Admin → List Values). Stored verbatim as
  // review_reason; reasonLabel decodes legacy key-based rows on display.
  const reasons = reasonChoices || (isClarify ? REQUEST_INFO_REASONS : REJECT_REASONS).map(r => r.label);
  const accent = isClarify ? '#E89A2E' : '#E0290A';
  const heading = isClarify ? 'Request info — ask the supplier to clarify' : 'Reject — tell the supplier why';
  const submitLabel = isClarify ? 'Send Request' : 'Reject Document';
  const submittingNow = (isClarify && submittingAction === 'request_info') || (!isClarify && submittingAction === 'reject');
  const canSubmit = !!reason && notes.trim().length >= MIN_NOTES_LENGTH && !submittingAction;
  return (
    <div className="rounded-md px-4 py-4" style={{ backgroundColor: `${accent}10`, border: `1px solid ${accent}40` }}>
      <p className="font-['Metropolis'] text-xs font-bold uppercase tracking-[0.18em] mb-3" style={{ color: accent }}>{heading}</p>
      <label className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">Reason</label>
      <select
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={!!submittingAction}
        className="w-full bg-white border border-line rounded-md px-3 py-2 font-['Metropolis'] text-sm text-ink focus:outline-none disabled:opacity-50 mb-3"
        style={{ borderColor: reason ? accent : '#D4D9C6' }}>
        <option value="">Choose a reason…</option>
        {reasons.map(label => <option key={label} value={label}>{label}</option>)}
      </select>
      <label className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">
        {isClarify ? 'What do you need clarified?' : 'What does the supplier need to do?'}
      </label>
      <AutoSizeTextarea
        minRows={4}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        disabled={!!submittingAction}
        placeholder={`Minimum ${MIN_NOTES_LENGTH} characters — the supplier sees this verbatim.`}
        className="w-full bg-white border border-line rounded-md px-3 py-2 font-['Metropolis'] text-sm text-ink focus:outline-none disabled:opacity-50" />
      <p className="font-['Metropolis'] text-[11px] mt-1 text-[#51534A]">
        {notes.trim().length === 0
          ? `At least ${MIN_NOTES_LENGTH} characters.`
          : notes.trim().length < MIN_NOTES_LENGTH
            ? `${MIN_NOTES_LENGTH - notes.trim().length} more character${MIN_NOTES_LENGTH - notes.trim().length === 1 ? '' : 's'}.`
            : `${notes.trim().length} characters.`}
      </p>
      <div className="flex items-center justify-end gap-3 mt-3">
        <button type="button" onClick={onCancel} disabled={!!submittingAction}
          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 disabled:opacity-50 disabled:cursor-not-allowed">
          Cancel
        </button>
        <ActionButton variant="solid" accent={accent} textOnAccent={isClarify ? '#131313' : '#F9FCF1'} onClick={onConfirm} disabled={!canSubmit}>
          {submittingNow ? 'Saving…' : submitLabel}
        </ActionButton>
      </div>
    </div>
  );
}
